import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Grid,
  InputAdornment,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import { api } from "../services/api";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/ToastContext";
import VolumeDiskDetails from "./VolumeDiskDetails";
import { PropTypes } from "prop-types";
import RoleGuard from "../components/RoleGuard";
import DiskEditHistory from "./DiskEditHistory";

function getFieldValue(resourceRequestDetails, fieldName) {
  const field = resourceRequestDetails?.find(
    (item) => item.fieldName?.toLowerCase() === fieldName?.toLowerCase()
  );
  return field ? field.fieldValue : null;
}

export default function DynamicDiskFormAWS({ resourceOverview, resourceTypeId }) {
  const selectedAccountId = localStorage.getItem("selectedAccountId");
  const formId = 68;

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disks, setDisks] = useState([]);
  const [errors, setErrors] = useState([]);
  const [diskPrices, setDiskPrices] = useState([]);
  const [totalMarketPrice, setTotalMarketPrice] = useState(0);
  const [totalDiscountPrice, setTotalDiscountPrice] = useState(0);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [priceList, setPriceList] = useState([]);
  const [operationDetails, setOperationDetails] = useState(null);

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingDiskId, setEditingDiskId] = useState(null);
  const [editingDiskConfig, setEditingDiskConfig] = useState(null);

  const toast = useToast();
  const navigate = useNavigate();

  const isRunning = ["running", "Running", " RUNNING"].includes(
    resourceOverview?.resourceStatus
  );
  const isStopped = ["stopped", "Stopped", " STOPPED", "Stop", " stop"].includes(
    resourceOverview?.resourceStatus
  );

  const organizationName = sessionStorage.getItem("organizationName") || "Organization";

  const locationObj = resourceOverview?.resourceRequestDetails?.find(
  (item) => item.fieldName?.toLowerCase() === "location"
);
  const region = locationObj ? locationObj.fieldValue : (resourceOverview?.region || "");

  const instanceId = useMemo(() => {
    return (
      resourceOverview?.awsInstanceId || 
      getFieldValue(resourceOverview?.resourceRequestDetails, "instanceId") ||
      getFieldValue(resourceOverview?.resourceRequestDetails, "instance_id") ||
      resourceOverview?.resource_Id ||
      ""
    );
  }, [resourceOverview]);

  useEffect(() => {
    const loadOperationDetails = async () => {
      try {
        const cloudProviderId = localStorage.getItem("selectedProviderId");
        const response = await api.get(
          `/api/Application/popularServices?cloudProviderId=${cloudProviderId}&serviceId=2&page=1&pageSize=10`
        );
        const operation = response.data?.find(
          (item) => item.resourceName?.toLowerCase() === "additional disks"
        );
        setOperationDetails(operation);
      } catch (error) {
        console.error("Failed to load Additional Disk operation", error);
      }
    };
    loadOperationDetails();
  }, []);

  useEffect(() => {
    if (!region) return;
    api
      .get(`/api/AwsEc2/additionalDiskPrice?region=${region}`)
      .then((res) => setPriceList(res.data || []))
      .catch(() => setPriceList([]));
  }, [region]);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/api/Resources/form/${formId}/${selectedAccountId}`)
      .then((res) => setFields(res.data || []))
      .catch(() => setFields([]))
      .finally(() => setLoading(false));
  }, [formId, selectedAccountId]);

  const getVolumeSizeField = () => fields.find((f) => f.fieldLabel?.toLowerCase().includes("size"));
  const getVolumeTypeField = () => fields.find((f) => f.fieldLabel?.toLowerCase().includes("type"));
  const getVolumeNameField = () => fields.find((f) => f.fieldLabel?.toLowerCase().includes("name"));
  const getIopsField = () => fields.find((f) => f.fieldLabel?.toLowerCase().includes("iops"));
  const getThroughputField = () => fields.find((f) => f.fieldLabel?.toLowerCase().includes("throughput"));

  const orderedFields = useMemo(() => {
    const order = [
      getVolumeNameField()?.fieldName,
      getVolumeTypeField()?.fieldName,
      getVolumeSizeField()?.fieldName,
      getIopsField()?.fieldName,
      getThroughputField()?.fieldName,
    ];

    return [...fields].sort((a, b) => {
      const indexA = order.indexOf(a.fieldName);
      const indexB = order.indexOf(b.fieldName);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return 0;
    });
  }, [fields]);

  const extractVolumeTypeKey = (rawValue) => {
  if (!rawValue) return "";
  const str = String(rawValue).trim().toLowerCase();
  if (str.includes("gp3")) return "gp3";
  if (str.includes("gp2")) return "gp2";
  if (str.includes("io1")) return "io1";
  if (str.includes("io2")) return "io2";

  const typeField = getVolumeTypeField();
  let options = [];
  try {
    options = JSON.parse(typeField?.dropdownValues || "[]");
  } catch {
    options = [];
  }

  const selectedOption = options.find((opt) => String(opt.value) === String(rawValue));
  const label = selectedOption ? selectedOption.label : String(rawValue);
  const start = label.indexOf("(");
  const end = label.indexOf(")", start + 1);
  return start !== -1 && end !== -1 ? label.slice(start + 1, end).trim().toLowerCase() : label.trim().toLowerCase();
};

  const getVolumeLimits = (volumeTypeValue) => {
    const typeField = getVolumeTypeField();
    if (!typeField || !typeField.dropdownValues) return {};
    try {
      const options = JSON.parse(typeField.dropdownValues);
      return options.find((opt) => String(opt.value) === String(volumeTypeValue)) || {};
    } catch {
      return {};
    }
  };

  const getDependentConfig = (field, dependentFieldValue) => {
    if (!field?.dependentValues || !dependentFieldValue) return null;
    return field.dependentValues.find((dv) => dv.value === dependentFieldValue) || null;
  };

  const createInitialDisk = () => {
    const initial = { id: crypto.randomUUID() };
    fields.forEach((f) => { initial[f.fieldName] = ""; });
    return initial;
  };

  const calculateDiskPriceFromList = (disk, diskIdx) => {
  const sizeField = getVolumeSizeField();
  const typeField = getVolumeTypeField();
  const iopsField = getIopsField();
  const throughputField = getThroughputField();

  const size = Number(disk[sizeField?.fieldName] || 0);
  const awsType = extractVolumeTypeKey(disk[typeField?.fieldName])?.toLowerCase();
  const normalizedRegion = region?.trim().toLowerCase();

  if (!awsType || !Number.isFinite(size) || size <= 0) {
    setDiskPrices((prev) => {
      const updated = [...prev];
      updated[diskIdx] = null;
      return updated;
    });
    return;
  }

  const relatedPrices = priceList.filter(
    (p) =>
      p.volumeType?.toLowerCase() === awsType &&
      (!normalizedRegion || p.regionCode?.toLowerCase() === normalizedRegion)
  );

  const storagePriceEntry = relatedPrices.find(
    (p) => p.unit?.toLowerCase() === "gb-month" || p.unit?.toLowerCase().includes("gb")
  );

  const defaultStoragePrice = awsType === "gp2" ? 0.10 : awsType === "gp3" ? 0.08 : 0.13;
  const storageUnitPrice = storagePriceEntry && Number.isFinite(Number(storagePriceEntry.pricePerUnit))
    ? Number(storagePriceEntry.pricePerUnit)
    : defaultStoragePrice;

  const discountPercent = Number(storagePriceEntry?.discount || 0);

  const rawIops = Number(disk[iopsField?.fieldName] || 0);
  const rawThroughput = Number(disk[throughputField?.fieldName] || 0);

  const currentIops = Number.isFinite(rawIops) ? Math.max(rawIops, 0) : 0;
  const currentThroughput = Number.isFinite(rawThroughput) ? Math.max(rawThroughput, 0) : 0;

  let storageCost = size * storageUnitPrice;
  let iopsCost = 0;
  let throughputCost = 0;

  if (awsType === "gp3") {
    const iopsPriceEntry = relatedPrices.find(
      (p) => p.unit?.toLowerCase() === "iops-month" || p.unit?.toLowerCase().includes("iops")
    );
    const throughputPriceEntry = relatedPrices.find(
      (p) => p.unit?.toLowerCase() === "mbps-month" || p.unit?.toLowerCase().includes("throughput")
    );

    const iopsPrice = iopsPriceEntry && Number.isFinite(Number(iopsPriceEntry.pricePerUnit))
      ? Number(iopsPriceEntry.pricePerUnit)
      : 0.01;

    const throughputPrice = throughputPriceEntry && Number.isFinite(Number(throughputPriceEntry.pricePerUnit))
      ? Number(throughputPriceEntry.pricePerUnit)
      : 0.04;

    const additionalIops = Math.max(currentIops - 3000, 0);
    iopsCost = additionalIops * iopsPrice;

    const additionalThroughput = Math.max(currentThroughput - 125, 0);
    throughputCost = additionalThroughput * throughputPrice;
  } else if (awsType === "io1") {
    const iopsPriceEntry = relatedPrices.find(
      (p) => p.unit?.toLowerCase() === "iops-month" || p.unit?.toLowerCase().includes("iops")
    );

    const iopsPrice = iopsPriceEntry && Number.isFinite(Number(iopsPriceEntry.pricePerUnit))
      ? Number(iopsPriceEntry.pricePerUnit)
      : 0.07;

    iopsCost = currentIops * iopsPrice;
  } else if (awsType === "io2") {
    const iopsTiers = relatedPrices
      .filter((p) => p.unit?.toLowerCase() === "iops-month" || p.unit?.toLowerCase().includes("iops"))
      .sort((a, b) => Number(a.tierMinIOPS || 0) - Number(b.tierMinIOPS || 0));

    const tier1 = iopsTiers.find((t) => t.priceTier?.toLowerCase() === "tier1");
    const tier2 = iopsTiers.find((t) => t.priceTier?.toLowerCase() === "tier2");
    const tier3 = iopsTiers.find((t) => t.priceTier?.toLowerCase() === "tier3");

    const t1Price = tier1 && Number.isFinite(Number(tier1.pricePerUnit)) ? Number(tier1.pricePerUnit) : 0.07;
    const t2Price = tier2 && Number.isFinite(Number(tier2.pricePerUnit)) ? Number(tier2.pricePerUnit) : 0.05;
    const t3Price = tier3 && Number.isFinite(Number(tier3.pricePerUnit)) ? Number(tier3.pricePerUnit) : 0.03;

    if (currentIops <= 32000) {
      iopsCost = currentIops * t1Price;
    } else if (currentIops <= 64000) {
      iopsCost = 32000 * t1Price + (currentIops - 32000) * t2Price;
    } else {
      iopsCost = 32000 * t1Price + 32000 * t2Price + (currentIops - 64000) * t3Price;
    }
  }

  const marketTotal = storageCost + iopsCost + throughputCost;

  if (!Number.isFinite(marketTotal)) {
    setDiskPrices((prev) => {
      const updated = [...prev];
      updated[diskIdx] = "Error";
      return updated;
    });
    return;
  }

  const discountAmount = marketTotal * (discountPercent / 100);
  const discountTotal = marketTotal - discountAmount;
  const round3 = (num) => Number(Number(num).toFixed(3));

  setDiskPrices((prev) => {
    const updated = [...prev];
    updated[diskIdx] = {
      size,
      awsType,
      marketTotal: round3(marketTotal),
      discountTotal: round3(discountTotal),
      marketRatePerMonth: round3(marketTotal),
      discountPricePerMonth: round3(discountTotal),
    };
    return updated;
  });
};

  useEffect(() => {
    const marketTotal = diskPrices.reduce((sum, priceObj) => {
      if (priceObj && typeof priceObj === "object" && priceObj.marketRatePerMonth) {
        return sum + Number(priceObj.marketRatePerMonth);
      }
      return sum + (Number.parseFloat(priceObj) || 0);
    }, 0);

    const discountTotal = diskPrices.reduce((sum, priceObj) => {
      if (priceObj && typeof priceObj === "object" && priceObj.discountPricePerMonth) {
        return sum + Number(priceObj.discountPricePerMonth);
      }
      return sum + (Number.parseFloat(priceObj) || 0);
    }, 0);

    setTotalMarketPrice(marketTotal);
    setTotalDiscountPrice(discountTotal);
  }, [diskPrices]);

  const handleChange = (diskIdx, name, value) => {
  const volumeSizeField = getVolumeSizeField();
  const volumeTypeField = getVolumeTypeField();
  const iopsField = getIopsField();
  const throughputField = getThroughputField();

  let processedValue = value;
  const isSize = volumeSizeField && name === volumeSizeField.fieldName;
  const isIops = iopsField && name === iopsField.fieldName;
  const isThroughput = throughputField && name === throughputField.fieldName;

  if (isSize || isIops || isThroughput) {
    processedValue = processedValue.replaceAll(/\D/g, "");
    if (processedValue.length > 1 && processedValue[0] === "0") {
      processedValue = processedValue.replace(/^0+/, "");
    }
  }

  const currentDisk = disks[diskIdx] || {};
  const selectedVolumeType = extractVolumeTypeKey(
    name === volumeTypeField?.fieldName ? processedValue : currentDisk[volumeTypeField?.fieldName]
  );

  const updatedDisks = disks.map((disk, idx) => {
    if (idx !== diskIdx) return disk;
    const updated = { ...disk, [name]: processedValue };

    if (volumeTypeField && name === volumeTypeField.fieldName) {
      const typeKey = extractVolumeTypeKey(processedValue);
      
      if (iopsField) {
        const iopsConfig = getDependentConfig(iopsField, typeKey);
        updated[iopsField.fieldName] = iopsConfig ? iopsConfig.defaultValue : ""; 
      }
      if (throughputField) {
        const tpConfig = getDependentConfig(throughputField, typeKey);
        updated[throughputField.fieldName] = tpConfig ? tpConfig.defaultValue : "";
      }
    }
    return updated;
  });

  setDisks(updatedDisks);

  setErrors((prev) =>
    prev.map((err, idx) => {
      if (idx !== diskIdx) return err;
      const newErr = { ...err };
      const targetDisk = updatedDisks[diskIdx];
      const sizeVal = Number.parseInt(targetDisk[volumeSizeField?.fieldName], 10) || 0;
      const limits = getVolumeLimits(selectedVolumeType);

      if (isSize) {
        const minSize = limits.minSizeGiB || 1;
        const dynamicMaxSize = limits.maxSizeGiB || 65536;
        const parsedVal = Number.parseInt(processedValue, 10);
        
        if (!processedValue || parsedVal < minSize) {
          newErr[name] = `Min: ${minSize} GiB required`;
        } else if (parsedVal > dynamicMaxSize) {
          newErr[name] = `Maximum allowed is ${dynamicMaxSize.toLocaleString()} GiB`;
        } else {
          delete newErr[name];
        }
      }

      if (isIops || isSize) {
        const iopsVal = Number.parseInt(targetDisk[iopsField?.fieldName], 10);
        const iopsFieldName = iopsField?.fieldName;

        if (limits.maxIOPS) {
           const maxIopsAllowed = sizeVal ? Math.min(limits.maxIOPS, Math.max(limits.minIOPS, sizeVal * limits.maxIOPSPerGiB)) : limits.maxIOPS;
           const minIops = limits.minIOPS || 1;
           
           if (!targetDisk[iopsFieldName]) newErr[iopsFieldName] = "IOPS is required";
           else if (iopsVal < minIops) {
             newErr[iopsFieldName] = `Min: ${minIops.toLocaleString()} IOPS required`;
           }
           else if (sizeVal && iopsVal > maxIopsAllowed) newErr[iopsFieldName] = `Maximum allowed IOPS for a size ${sizeVal} GiB volume is ${maxIopsAllowed.toLocaleString()}.`;
           else delete newErr[iopsFieldName];
        }
      }

      if (isThroughput || isIops || isSize) {
        const tpVal = Number.parseInt(targetDisk[throughputField?.fieldName], 10);
        const currentIops = Number.parseInt(targetDisk[iopsField?.fieldName] || limits.minIOPS || "3000", 10);
        const minTp = limits.minThroughput || 1;

        if (limits.maxThroughput) {
           const maxTpAllowed = Math.min(limits.maxThroughput, Math.max(limits.minThroughput, Math.floor(currentIops * (limits.maxThroughputPerIOPS || 0.25))));
           
           if (!targetDisk[throughputField?.fieldName]) {
               newErr[throughputField?.fieldName] = "Throughput is required";
           }
           else if (tpVal < minTp) {
               newErr[throughputField?.fieldName] = `Min: ${minTp} MiB/s required`;
           }
           else if (tpVal > maxTpAllowed) {
               newErr[throughputField?.fieldName] = `Maximum allowed Throughput for ${currentIops} IOPS is ${maxTpAllowed} MiB/s.`;
           }
           else {
               delete newErr[throughputField?.fieldName];
           }
        }
      }

      return newErr;
    })
  );

  calculateDiskPriceFromList(updatedDisks[diskIdx], diskIdx);
};

  const handleOpenAddForm = () => {
    setEditingDiskId(null);
    setEditingDiskConfig(null);
    setDisks([createInitialDisk()]);
    setErrors([{}]);
    setDiskPrices([null]);
    setIsFormVisible(true);
  };

 const handleEditDisk = async (diskToEdit) => {
    if (diskToEdit.isEditable === false || diskToEdit.isEditInProgress === true) {
      toast.error("This volume cannot be edited at this time. Edit is either disabled or already in progress.");
      return;
    }

    let fetchedDetails = null;
    if (diskToEdit.resourceVolumeId) {
      try {
        const res = await api.get(`/api/Resources/GetAdditionalDiskEditDetails/${diskToEdit.resourceVolumeId}`);
        fetchedDetails = res.data;
      } catch (error) {
        console.error("Failed to fetch detailed edit volume configuration", error);
        toast.error("Failed to fetch detailed volume configuration.");
        return;
      }
    }

    const getFetchedValue = (f) => {
      if (!fetchedDetails) return null;

      if (Array.isArray(fetchedDetails)) {
        const field = fetchedDetails.find(
          (item) => 
            item.fieldName?.toLowerCase() === f.fieldName?.toLowerCase() ||
            item.fieldName?.toLowerCase() === f.fieldLabel?.toLowerCase() ||
            item.fieldName?.toLowerCase().replace(/\s/g, '') === f.fieldName?.toLowerCase().replace(/\s/g, '')
        );
        return field ? field.fieldValue : null;
      } else {
        const keysToTry = [
          f.fieldName, 
          f.fieldLabel, 
          f.fieldName?.replace(/\s/g, ''),
          f.fieldName?.toLowerCase() === 'name' ? 'volumeName' : null,
          f.fieldName?.toLowerCase().includes('size') ? 'volumeSize' : null,
          f.fieldName?.toLowerCase().includes('type') ? 'volumeType' : null
        ].filter(Boolean).map(k => k.toLowerCase());

        const matchedKey = Object.keys(fetchedDetails).find(k => keysToTry.includes(k.toLowerCase()));
        return matchedKey ? fetchedDetails[matchedKey] : null;
      }
    };

    const getDropdownValueFromLabel = (field, labelOrValue) => {
      if (field.fieldType === "dropdown") {
        try {
          const options = JSON.parse(field.dropdownValues || "[]");
          const option = options.find(opt => opt.label === labelOrValue || opt.value === labelOrValue);
          return option ? option.value : labelOrValue;
        } catch {
          return labelOrValue;
        }
      }
      return labelOrValue;
    };

    const editInitial = { id: diskToEdit.id || crypto.randomUUID() };

    fields.forEach((f) => {
      const fieldKey = f.fieldName;
      let rawVal = getFetchedValue(f) || diskToEdit[fieldKey] || diskToEdit[f.fieldLabel] || "";
      editInitial[fieldKey] = getDropdownValueFromLabel(f, rawVal);
    });

    const nameField = getVolumeNameField();
    if (nameField && editInitial[nameField.fieldName] && instanceId) {
      const prefix = `${instanceId}-`;
      if (editInitial[nameField.fieldName].startsWith(prefix)) {
        editInitial[nameField.fieldName] = editInitial[nameField.fieldName].replace(prefix, "");
      }
    }

    setEditingDiskConfig(diskToEdit);
    setEditingDiskId(diskToEdit.resourceVolumeId || diskToEdit.volumeId || diskToEdit.id || "EDIT");
    setDisks([editInitial]);
    setErrors([{}]);
    setDiskPrices([null]);
    setIsFormVisible(true);
    calculateDiskPriceFromList(editInitial, 0);
  };

  const handleDeleteDisk = (diskIdx) => {
    setDisks((prev) => prev.filter((_, idx) => idx !== diskIdx));
    setErrors((prev) => prev.filter((_, idx) => idx !== diskIdx));
    setDiskPrices((prev) => prev.filter((_, idx) => idx !== diskIdx));
  };

  const handleReset = () => {
    setDisks([]);
    setErrors([]);
    setDiskPrices([]);
    setTotalMarketPrice(0);
    setTotalDiscountPrice(0);
    setEditingDiskId(null);
    setEditingDiskConfig(null);
    setIsFormVisible(false);
  };

  const validate = () => {
  const sizeField = getVolumeSizeField();
  const typeField = getVolumeTypeField();
  const iopsField = getIopsField();
  const throughputField = getThroughputField();

  const allErrors = disks.map((disk) => {
    const err = {};
    const currentType = extractVolumeTypeKey(disk[typeField?.fieldName]);
    const limits = getVolumeLimits(currentType);
    const sizeVal = disk[sizeField?.fieldName];
    const parsedSize = Number.parseInt(sizeVal, 10);

    fields.forEach((f) => {
      let isVisible = true;
      if (f.dependentOn === typeField?.fieldName) {
         const dependentConfig = getDependentConfig(f, currentType);
         if (!dependentConfig || !dependentConfig.isVisible) {
           isVisible = false;
         }
      }

      if (f.isRequired && isVisible && !disk[f.fieldName]) {
        err[f.fieldName] = "Required";
      }
    });

    if (sizeVal !== undefined && sizeVal !== "") {
      const minSize = limits.minSizeGiB || 1;
      const dynamicMaxSize = limits.maxSizeGiB || 65536;

      if (!/^\d+$/.test(sizeVal)) err[sizeField.fieldName] = "Only numbers allowed";
      else if (parsedSize < minSize) {
        err[sizeField.fieldName] = `Minimum allowed is ${minSize} GiB`;
      }
      else if (parsedSize > dynamicMaxSize) {
        err[sizeField.fieldName] = `Maximum allowed is ${dynamicMaxSize.toLocaleString()} GiB`;
      }
    }

    if (iopsField && disk[iopsField.fieldName] !== undefined && limits.maxIOPS) {
      const iopsVal = Number.parseInt(disk[iopsField.fieldName], 10);
      const maxIopsAllowed = parsedSize ? Math.min(limits.maxIOPS, Math.max(limits.minIOPS, parsedSize * limits.maxIOPSPerGiB)) : limits.maxIOPS;
      const minIops = limits.minIOPS || 1;
      
      if (!disk[iopsField.fieldName]) err[iopsField.fieldName] = "IOPS is required";
      else if (iopsVal < minIops) err[iopsField.fieldName] = `Min: ${minIops.toLocaleString()} IOPS required`;
      else if (iopsVal > maxIopsAllowed) {
          err[iopsField.fieldName] = `Maximum allowed IOPS for a size ${parsedSize} GiB volume is ${maxIopsAllowed.toLocaleString()}.`;
      }
    }

    if (throughputField && disk[throughputField.fieldName] !== undefined && limits.maxThroughput) {
      const throughputVal = Number.parseInt(disk[throughputField.fieldName], 10);
      const currentIops = Number.parseInt(disk[iopsField?.fieldName] || limits.minIOPS || "3000", 10);
      const maxTpAllowed = Math.min(limits.maxThroughput, Math.max(limits.minThroughput, Math.floor(currentIops * (limits.maxThroughputPerIOPS || 0.25))));
      const minTp = limits.minThroughput || 1;
      
      if (!disk[throughputField.fieldName]) err[throughputField.fieldName] = "Throughput is required";
      else if (throughputVal < minTp) {
          err[throughputField.fieldName] = `Min: ${minTp} MiB/s required`;
      }
      else if (throughputVal > maxTpAllowed) {
          err[throughputField.fieldName] = `Maximum allowed Throughput for ${currentIops} IOPS is ${maxTpAllowed} MiB/s.`;
      }
    }

    return err;
  });

  setErrors(allErrors);
  return allErrors.every((e) => Object.keys(e).length === 0);
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitLoading(true);

    const applicationId = getFieldValue(resourceOverview.resourceRequestDetails, "applicationId");
    const environment = getFieldValue(resourceOverview.resourceRequestDetails, "environment");
    const resourceNameConstraint = getFieldValue(resourceOverview.resourceRequestDetails, "resourceName");
    const cloudProvider = localStorage.getItem("selectedProviderId");
    const subscription = localStorage.getItem("selectedAccountId") || "";
    const requester = sessionStorage.getItem("userId");
    const parent_Resource_ID = resourceOverview?.resource_Id || "";
    const applicationName = resourceOverview?.applicationName || "";
    
    const nameField = getVolumeNameField();
    const typeField = getVolumeTypeField();

    const parseDropdownValues = (dropdownValuesStr) => {
      try { return JSON.parse(dropdownValuesStr); } catch { return []; }
    };

    const getDropdownFieldValue = (field, diskValue) => {
      const options = parseDropdownValues(field.dropdownValues);
      const selectedOption = options.find((opt) => String(opt.value) === String(diskValue));
      return selectedOption ? selectedOption.label : diskValue;
    };

    const buildFieldPayload = (field, diskValues) => {
      const fieldValue = diskValues[field.fieldName];
      if (field.fieldType === "dropdown") return getDropdownFieldValue(field, fieldValue);
      return fieldValue || "";
    };

    const disksPayload = disks.map((disk) => {
      const diskPayload = {};
      const currentType = extractVolumeTypeKey(disk[typeField?.fieldName]);

      fields.forEach((field) => {
        let isVisible = true;
        if (field.dependentOn === typeField?.fieldName) {
           const dependentConfig = getDependentConfig(field, currentType);
           if (!dependentConfig || !dependentConfig.isVisible) isVisible = false;
        }

        if (!isVisible) return;

        if (field.fieldName === nameField?.fieldName) {
          diskPayload[field.fieldName] = instanceId ? `${instanceId}-${disk[field.fieldName]}` : disk[field.fieldName];
        } else {
          diskPayload[field.fieldName] = buildFieldPayload(field, disk);
        }
      });

      return diskPayload;
    });

    const payload = {
      applicationName,
      subscription,
      totalPrice: totalDiscountPrice.toFixed(2),
      resourceRequest: JSON.stringify({ disks: disksPayload }),
      requester,
      resourceType: operationDetails?.parentResourceTypeId || resourceTypeId,
      organizationId: Number.parseInt(sessionStorage.getItem("organizationId"), 10),
      parent_Resource_ID: Number.parseInt(parent_Resource_ID, 10) || 0,
      region,
      cloudProvider,
      marketPrice: totalMarketPrice.toFixed(2),
      operationId: editingDiskId ? 84 : (operationDetails?.id || 68),
      operationName: editingDiskId ? "Additional Disk Edit" : (operationDetails?.resourceName || "Additional Disk"),
      isApprovedRequest: "true",
      applicationId,
      environment,
      resourceNameConstraint,
      priority: "4",
    };

    if (editingDiskId && editingDiskConfig) {
      payload.resourceVolumeId = editingDiskConfig.resourceVolumeId || 0;
      payload.resource_Id = editingDiskConfig.childResourceId || null;
    }

    try {
      const endpoint = editingDiskId ? "/api/Resources/UpdateAdditionalDisk" : "/api/Resources/createOperationalRequest";
      const response = await api.post(endpoint, payload, { headers: { "Content-Type": "application/json" } });
      toast.success(response?.data?.message || (editingDiskId ? "Disk update requested successfully." : "Operation completed successfully."));
      handleReset();
      setTimeout(() => navigate("/provision-status"), 300);
    } catch (error) {
      toast.error(error.message || "Failed to process resource request.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderFooterPrice = () => {
    if (diskPrices.length === 0 || diskPrices.every(p => p === null)) {
      return <Typography color="textSecondary" variant="body2">Select Volume Type to see price.</Typography>;
    }
    if (diskPrices.includes("Error")) {
      return <Typography color="error" variant="body2">Unable to fetch price.</Typography>;
    }

    const totalSize = diskPrices.reduce((acc, curr) => acc + (curr && curr.size ? curr.size : 0), 0);

    return (
      <>
        <Typography variant="body2" sx={{ color: "green", fontWeight: "bold" }}>
          Forecasted Price ({totalSize} GB/Month): <b>${totalDiscountPrice.toFixed(3)}</b>
        </Typography>
      </>
    );
  };

  if (loading) return <CircularProgress />;
  const isResourceAddedFromCMP = resourceOverview?.isResourceAddedFromCMP === true;

  if (!isResourceAddedFromCMP) {
    return (
      <Box sx={{ p: 3 }}>
        <VolumeDiskDetails resourceOverview={resourceOverview} onEditDisk={handleEditDisk} />
      </Box>
    );
  }

  if (!isRunning && !isStopped) {
    return (
      <Box sx={{ p: 3 }}>
        <VolumeDiskDetails resourceOverview={resourceOverview} onEditDisk={handleEditDisk} />
        {!resourceOverview?.resourceStatus || resourceOverview?.resourceStatus.trim() === "" ? (
          <Alert severity="error" sx={{ mt: 1, display: "flex", justifyContent: "center" }}>The resource is yet to be provisioned. Additional disks cannot be added at this time.</Alert>
        ) : (
          <Alert severity="info" sx={{ mt: 1, display: "flex", justifyContent: "center" }}>{`The Resource status is ${resourceOverview?.resourceStatus}. Additional disks cannot be added at this time.`}</Alert>
        )}
      </Box>
    );
  }

  const volumeSizeField = getVolumeSizeField();
  const volumeNameField = getVolumeNameField();
  const typeField = getVolumeTypeField();
  const iopsField = getIopsField();
  const throughputField = getThroughputField();

 let isSubmitDisabled = submitLoading;
  
  const hasActiveErrors = errors.some((err) => err && Object.keys(err).length > 0);
  if (hasActiveErrors) {
    isSubmitDisabled = true;
  }
  
  if (!isSubmitDisabled && editingDiskId && disks.length > 0) {
    const currentDisk = disks[0];
    const currentVolumeType = extractVolumeTypeKey(currentDisk[typeField?.fieldName]);

    const originalVolume = resourceOverview?.resourceVolumeDetails?.find(
      (v) => 
        String(v.resourceVolumeId) === String(editingDiskId) || 
        String(v.volumeId) === String(editingDiskId) || 
        String(v.id) === String(editingDiskId)
    );

    if (originalVolume) {
      let hasChanges = false;

      const sizeField = getVolumeSizeField();
      const currentSize = Number(currentDisk[sizeField?.fieldName]) || 0;
      const origSize = Number(originalVolume.volumeSize) || 0;
      if (currentSize !== origSize) hasChanges = true;

      const iopsField = getIopsField();
      if (iopsField) {
        const iopsConfig = getDependentConfig(iopsField, currentVolumeType);
        const isIopsVisible = !iopsField.dependentOn || (iopsConfig && iopsConfig.isVisible);
        if (isIopsVisible) {
          const currentIops = Number(currentDisk[iopsField.fieldName]) || 0;
          const origIops = Number(originalVolume.iops) || 0;
          if (currentIops !== origIops) hasChanges = true;
        }
      }

      const tpField = getThroughputField();
      if (tpField) {
        const tpConfig = getDependentConfig(tpField, currentVolumeType);
        const isTpVisible = !tpField.dependentOn || (tpConfig && tpConfig.isVisible);
        if (isTpVisible) {
          const currentTp = Number(currentDisk[tpField.fieldName]) || 0;
          const origTp = Number(originalVolume.throughput) || 0;
          if (currentTp !== origTp) hasChanges = true;
        }
      }

      if (!hasChanges) {
        isSubmitDisabled = true;
      }
    }
  }

 return (
    <Box sx={{ maxWidth: "auto", mx: "auto"}}>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
        <Typography variant="h6" fontWeight="600" sx={{py:1}}>Volume Details</Typography>
        
        {!isFormVisible && (
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenAddForm}>
            Configure AWS Storage
          </Button>
        )}
      </Box>

      <VolumeDiskDetails resourceOverview={resourceOverview} onEditDisk={handleEditDisk} />

      {isFormVisible && (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4, border: "1px solid", fontWeight:"Bold", borderColor: (t) => (t.palette.mode === "dark" ? "divider" : "#e0e0e0"), borderRadius: 2, backgroundColor: (t) => (t.palette.mode === "dark" ? "background.paper" : "#fafafa") }}>
          <Typography sx={{px:3, pt:2}} variant="h6" fontWeight="600" mb={2}>{editingDiskId ? "Edit Volume Details" : "Configure AWS Storage"}</Typography>
          
          {disks.map((disk, diskIdx) => {
            const currentVolumeType = extractVolumeTypeKey(disk[typeField?.fieldName]);
            const limits = getVolumeLimits(currentVolumeType);
            const sizeVal = Number.parseInt(disk[volumeSizeField?.fieldName], 10) || 0;
            
            return (
              <Box key={disk.id || diskIdx} sx={{p: 3,  position: "relative", background: (t) => (t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "#ffffff") }}>
                <Grid container spacing={2.5}>
                  
                  {orderedFields.map((field) => {
                    const isNameField = field.fieldName === volumeNameField?.fieldName;
                    const isTypeField = field.fieldName === typeField?.fieldName;

                    let isVisible = true;
                    let errorMessage = errors[diskIdx]?.[field.fieldName];
                    let baseHelperText = "";

                    if (field.dependentOn && field.dependentOn === typeField?.fieldName) {
                       const dependentConfig = getDependentConfig(field, currentVolumeType);
                       if (!dependentConfig || !dependentConfig.isVisible) {
                         isVisible = false;
                       } else {
                         if (field.fieldName === iopsField?.fieldName && limits.maxIOPS) {
                           const maxIopsAllowed = sizeVal ? Math.min(limits.maxIOPS, Math.max(limits.minIOPS, sizeVal * limits.maxIOPSPerGiB)) : limits.maxIOPS;
                           const minIops = limits.minIOPS || 1;
                           baseHelperText = `Min: ${minIops.toLocaleString()} IOPS, Max: ${maxIopsAllowed.toLocaleString()} IOPS.`;
                         } else if (field.fieldName === throughputField?.fieldName && limits.maxThroughput) {
                           const currentIops = Number.parseInt(disk[iopsField?.fieldName] || limits.minIOPS || "3000", 10);
                           const maxTpAllowed = Math.min(limits.maxThroughput, Math.max(limits.minThroughput, Math.floor(currentIops * (limits.maxThroughputPerIOPS || 0.25))));
                           const minTp = limits.minThroughput || 1;
                           baseHelperText = `Min: ${minTp} MiB/s, Max: ${maxTpAllowed} MiB/s.`;
                         } else {
                           baseHelperText = dependentConfig.validationMessage;
                         }
                       }
                    }

                    if (field.fieldName === volumeSizeField?.fieldName && limits.maxSizeGiB) {
                       const minSize = limits.minSizeGiB || 1;
                       baseHelperText = `Min: ${minSize} GiB, Max: ${limits.maxSizeGiB.toLocaleString()} GiB.`;
                    }

                    if (!isVisible) return null;

                    const hasError = !!errorMessage;

                   if (field.fieldType === "dropdown") {
                      let options = [];
                      try { options = JSON.parse(field.dropdownValues || "[]"); } catch { options = []; }
                      
                      return (
                        <Grid item xs={12} sm={4} key={field.fieldId}>
                          <TextField 
                            select 
                            fullWidth 
                            required={field.isRequired} 
                            label={field.fieldLabel} 
                            value={disk[field.fieldName] || ""} 
                            onChange={(e) => handleChange(diskIdx, field.fieldName, e.target.value)} 
                            error={hasError}
                            disabled={!!editingDiskId && isTypeField}
                            sx={{ width: '100%' }}
                            SelectProps={{ sx: { minWidth: 200 } }}
                            helperText={
                              (errorMessage || baseHelperText) ? (
                                <Box component="span" sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                                  {errorMessage && (
                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main', fontSize: '0.75rem', lineHeight: 1.2, mb: baseHelperText ? 0.5 : 0 }}>
                                      <CancelIcon fontSize="inherit" /> {errorMessage}
                                    </Box>
                                  )}
                                  {baseHelperText && (
                                    <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1.2 }}>
                                      {baseHelperText}
                                    </Box>
                                  )}
                                </Box>
                              ) : null
                            }
                          >
                            {options.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      );
                    }

                    return (
                      <Grid item xs={12} sm={6} md={4} key={field.fieldId}>
                        <TextField
                          fullWidth
                          label={field.fieldLabel}
                          required={field.isRequired}
                          value={disk[field.fieldName] || ""}
                          onChange={(e) => handleChange(diskIdx, field.fieldName, e.target.value)}
                          disabled={!!editingDiskId && isNameField}
                          sx={{ display: 'flex', width: '100%', minWidth: '100%' }}
                          slotProps={{
                            input: {
                              startAdornment: isNameField && instanceId ? (
                                <InputAdornment position="start">
                                  <Typography variant="body2" sx={{ color: "text.disabled", fontWeight: 600, userSelect: "none" }}>{instanceId}-</Typography>
                                </InputAdornment>
                              ) : null,
                              endAdornment: field.fieldLabel?.toLowerCase().includes("size") ? <InputAdornment position="end">GiB</InputAdornment> : field.fieldLabel?.toLowerCase().includes("throughput") ? <InputAdornment position="end">MiB/s</InputAdornment> : null,
                            },
                          }}
                          error={hasError}
                          helperText={
                            (errorMessage || baseHelperText) ? (
                              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                                {errorMessage && (
                                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main', fontSize: '0.75rem', lineHeight: 1.2, mb: baseHelperText ? 0.5 : 0 }}>
                                    <CancelIcon fontSize="inherit" /> {errorMessage}
                                  </Box>
                                )}
                                {baseHelperText && (
                                  <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1.2 }}>
                                    {baseHelperText}
                                  </Box>
                                )}
                              </Box>
                            ) : null
                          }
                        />
                      </Grid>
                    );
                  })}
                </Grid>

                {disks.length > 1 && !editingDiskId && (
                  <IconButton aria-label="Delete Disk" color="error" size="small" onClick={() => handleDeleteDisk(diskIdx)} sx={{ position: "absolute", top: 8, right: 8 }}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            );
          })}

          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ position: "sticky", bottom: 0, paddingY: 2, zIndex: 10, px: 3, bgcolor: (t) => t.palette.mode === "dark" ? "background.paper" : "#fafafa", borderTop: (t) => t.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>
            <Box mt={1}>
              {renderFooterPrice()}
            </Box>
            <Box display="flex" gap={2}>
              <Button type="button" variant="outlined" onClick={handleReset}>Cancel</Button>
              <RoleGuard requiredPermission="CREATE_ADDITIONAL_DISK">
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  disabled={isSubmitDisabled} 
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  {submitLoading ? <><CircularProgress size={20} color="inherit" />Submitting...</> : editingDiskId ? "Update Disk" : "Submit"}
                </Button>
              </RoleGuard>
            </Box>
          </Box>
        </Box>
      )}
      
      <DiskEditHistory parentResourceId={resourceOverview?.resource_Id} />

    </Box>
  );
}

DynamicDiskFormAWS.propTypes = {
  resourceOverview: PropTypes.shape({
    resourceStatus: PropTypes.string,
    resource_Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    applicationName: PropTypes.string,
    applicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isResourceAddedFromCMP: PropTypes.bool,
    resourceRequestDetails: PropTypes.arrayOf(PropTypes.shape({ fieldName: PropTypes.string, fieldValue: PropTypes.any })),
  }).isRequired,
  resourceTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};